import { motion } from "framer-motion";
import { ClipboardList, MessageSquare, Truck, Sparkles, ArrowUpRight } from "lucide-react";

const STEPS = [
  {
    num: "01",
    icon: ClipboardList,
    title: "Pick Your Service",
    text: "Choose Basic Clean, Full Detail, or Disaster Detail. Not sure? Text us a photo of your vehicle and we'll recommend the right one.",
  },
  {
    num: "02",
    icon: MessageSquare,
    title: "Call Or Text Us",
    text: "Reach out to 919-737-3526 with your package, vehicle type, and address. We'll lock in a time that works for you.",
  },
  {
    num: "03",
    icon: Truck,
    title: "We Arrive Ready To Work",
    text: "Our mobile setup rolls up to your home or office with every tool and pro-grade product needed. All we ask: access to a water spigot and a power outlet.",
  },
  {
    num: "04",
    icon: Sparkles,
    title: "Inspect & Enjoy",
    text: "Walk around your vehicle with us, check every panel and seam, then enjoy that showroom shine without leaving your driveway.",
  },
];

export default function HowItWorks({ onBook }) {
  return (
    <section id="process" data-testid="process-section" className="relative py-24 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-16 sm:mb-20"
        >
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400" data-testid="process-eyebrow">
            03 — Process
          </span>
          <h2 className="mt-4 font-syne font-extrabold uppercase tracking-tight leading-none text-4xl sm:text-6xl text-white">
            One Text. <span className="text-amber-400">Zero Effort.</span>
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-14">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.1, duration: 0.7 }}
              data-testid={`process-step-${s.num}`}
              className="group relative border-t border-white/10 pt-8 hover:border-amber-500/40 transition-colors duration-500"
            >
              <div className="flex items-start justify-between gap-6">
                <span className="font-syne font-extrabold text-6xl sm:text-7xl leading-none text-stroke group-hover:text-amber-400 group-hover:[-webkit-text-stroke:0px] transition-all duration-500">
                  {s.num}
                </span>
                <div className="h-12 w-12 rounded-full border border-white/10 flex items-center justify-center text-amber-400 group-hover:bg-amber-500 group-hover:text-black group-hover:border-amber-500 transition-colors duration-500 shrink-0">
                  <s.icon size={20} />
                </div>
              </div>
              <h3 className="mt-6 font-syne font-bold text-xl sm:text-2xl uppercase text-white">
                {s.title}
              </h3>
              <p className="mt-3 text-slate-400 text-sm sm:text-base leading-relaxed max-w-md">
                {s.text}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-16 flex justify-center"
        >
          <button
            onClick={onBook}
            data-testid="process-book-button"
            className="group inline-flex items-center gap-3 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-bold px-9 py-4 transition-colors duration-300"
          >
            Start With Step One
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
